import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'

import { Button } from './Button'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface TablePaginationProps<TData> {
  table: Table<TData>
}

export function TablePagination<TData>({ table }: TablePaginationProps<TData>) {
  const currentIndex = table.getState().pagination.pageIndex
  const pageCount = table.getPageCount()
  const indexArray = Array.from({ length: pageCount }, (_, i) => i)

  return (
    <Pagination className="flex w-full items-center justify-end gap-8">
      <PaginationContent>
        <PaginationItem>
          <Button
            className="h-8 w-8 p-0 lg:flex"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.setPageIndex(0)}
            variant="outlineSecondary"
          >
            <DoubleArrowLeftIcon className="h-4 w-4" />
          </Button>
        </PaginationItem>
        <PaginationItem>
          <Button
            className="gap-1.5 pl-2.5"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            variant="outlineSecondary"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span>Anterior</span>
          </Button>
        </PaginationItem>
        {currentIndex > 1 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        {indexArray.map((i) =>
          i >= currentIndex - 1 && i <= currentIndex + 1 ? (
            <PaginationItem key={i}>
              <Button
                onClick={() => table.setPageIndex(i)}
                variant={i === currentIndex ? 'active' : 'outlineSecondary'}
              >
                <span>{i + 1}</span>
              </Button>
            </PaginationItem>
          ) : null
        )}
        {currentIndex < pageCount - 2 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        <PaginationItem>
          <Button
            className="gap-1.5 pl-2.5"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            variant="outlineSecondary"
          >
            <span>Siguiente</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </PaginationItem>
        <PaginationItem>
          <Button
            className="hidden h-8 w-8 p-0 lg:flex"
            disabled={!table.getCanNextPage()}
            onClick={() => table.setPageIndex(pageCount - 1)}
            variant="outlineSecondary"
          >
            <DoubleArrowRightIcon className="h-4 w-4" />
          </Button>
        </PaginationItem>
      </PaginationContent>
      <Select
        onValueChange={(value) => {
          table.setPageSize(Number(value))
        }}
        value={`${table.getState().pagination.pageSize}`}
      >
        <SelectTrigger className="h-8 w-[70px]">
          <SelectValue placeholder={table.getState().pagination.pageSize} />
        </SelectTrigger>
        <SelectContent side="top" className="bg-white">
          {[5, 10, 20, 30, 40, 50].map((pageSize) => (
            <SelectItem key={pageSize} value={`${pageSize}`}>
              {pageSize}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Pagination>
  )
}
